import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { MapPin, Phone, Clock } from "lucide-react";

const getBranchMapUrl = (b) => {
  const name = (b.name || "").toLowerCase();
  const slug = (b.slug || "").toLowerCase();
  if (name.includes("lajpat") || slug.includes("lajpat")) return "https://maps.app.goo.gl/65Dwm9Nt6GAhpuoL6";
  if (name.includes("rajouri") || slug.includes("rajouri")) return "https://maps.app.goo.gl/5ChSfibhJxcBDTvH6";
  return "https://maps.app.goo.gl/oT8hJtohHm2YajVy6";
};

const getBranchTheme = (b, index) => {
  const name = (b.name || "").toLowerCase();
  const slug = (b.slug || "").toLowerCase();
  if (name.includes("lajpat") || slug.includes("lajpat") || index === 0) {
    return {
      cardBorder: "border-2 border-red-500/80 hover:border-red-400 shadow-xl shadow-red-950/30",
      eyebrow: "text-red-400 font-extrabold",
      title: "text-red-400",
      icon: "text-red-400",
      initial: { opacity: 0, x: -100 }
    };
  }
  if (name.includes("rajouri") || slug.includes("rajouri") || index === 1) {
    return {
      cardBorder: "border-2 border-amber-400/80 hover:border-amber-300 shadow-xl shadow-amber-950/30",
      eyebrow: "text-amber-400 font-extrabold",
      title: "text-amber-400",
      icon: "text-amber-400",
      initial: { opacity: 0, y: 100 }
    };
  }
  return {
    cardBorder: "border-2 border-emerald-400/80 hover:border-emerald-300 shadow-xl shadow-emerald-950/30",
    eyebrow: "text-emerald-400 font-extrabold",
    title: "text-emerald-400",
    icon: "text-emerald-400",
    initial: { opacity: 0, x: 100 }
  };
};

const marqueeBorders = [
  "border-2 border-red-500 shadow-lg shadow-red-950/40",
  "border-2 border-emerald-400 shadow-lg shadow-emerald-950/40",
  "border-2 border-amber-400 shadow-lg shadow-amber-950/40",
  "border-2 border-rose-400 shadow-lg shadow-rose-950/40",
  "border-2 border-hgold shadow-lg shadow-yellow-950/40",
  "border-2 border-cyan-400 shadow-lg shadow-cyan-950/40"
];

export default function Branches() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/branches")
      .then((r) => {
        setList(r.data);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  }, []);

  if (loading) {
    return (
      <div className="min-h-[80vh] flex flex-col items-center justify-center">
        <div className="premium-spinner mb-4"></div>
        <div className="label-eyebrow text-white/60">Loading Branches...</div>
      </div>
    );
  }

  return (
    <div data-testid="branches-page">
      {/* Hero Section */}
      <section className="relative min-h-[50vh] overflow-hidden -mt-[72px] flex items-center">
        <img src="/hero_image_hammam.png" alt="Branches" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/85 to-black/40" />
        <motion.div
          initial={{ opacity: 0, y: -100 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.25, 1, 0.5, 1] }}
          className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-40 pb-20 w-full"
        >
          <div className="label-eyebrow mb-6 text-emerald-400 font-extrabold tracking-widest uppercase">Best Gay Spa in New Delhi</div>
          <h1 className="text-5xl lg:text-7xl font-heading text-transparent bg-clip-text bg-gradient-to-r from-white via-amber-200 to-emerald-400 mb-6">
            Branches
          </h1>
          <p className="text-hsecondary text-lg max-w-2xl mt-4 leading-relaxed font-normal">
            Each of our three branches is designed as a distinct sanctuary — the shared soul of Hammam, expressed through the character of its neighbourhood.
          </p>
        </motion.div>
      </section>

      {/* Branch Cards Section */}
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {list.map((b, idx) => {
            const mapUrl = getBranchMapUrl(b);
            const theme = getBranchTheme(b, idx);

            return (
              <motion.div
                key={b.id}
                initial={theme.initial}
                whileInView={{ opacity: 1, x: 0, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, ease: [0.25, 1, 0.5, 1] }}
                data-testid={`branch-${b.slug}`}
                className={`group rounded-2xl bg-gradient-to-b from-hpaper via-black/90 to-hpaper ${theme.cardBorder} transition-all duration-300 overflow-hidden flex flex-col justify-between`}
              >
                <div className="aspect-video overflow-hidden bg-black/40">
                  <img
                    src={b.hero_image}
                    alt={b.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                </div>
                <div className="p-8 flex-1 flex flex-col justify-between">
                  <div>
                    <div className={`text-[11px] uppercase tracking-[0.25em] mb-2 ${theme.eyebrow}`}>
                      {b.city}
                    </div>
                    <h3 className={`font-heading text-3xl mb-4 font-bold ${theme.title}`}>
                      {b.name}
                    </h3>
                    <div className="space-y-3 text-sm text-hsecondary font-normal">
                      <div className="flex items-start gap-2.5">
                        <MapPin size={16} className={`${theme.icon} mt-0.5 shrink-0`} />
                        <a
                          href={mapUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="hover:underline hover:text-white transition-colors"
                          title="Open in Google Maps"
                        >
                          {b.address}
                        </a>
                      </div>
                      <div className="flex items-start gap-2.5">
                        <Phone size={16} className={`${theme.icon} mt-0.5 shrink-0`} />
                        <div className="flex flex-col gap-1">
                          <a
                            href={`tel:${(b.phone || "").replace(/\s+/g, "")}`}
                            className="hover:underline hover:text-white transition-colors"
                          >
                            {b.phone}
                          </a>
                          {b.phone2 ? (
                            <a
                              href={`tel:${(b.phone2 || "").replace(/\s+/g, "")}`}
                              className="hover:underline hover:text-white transition-colors"
                            >
                              {b.phone2}
                            </a>
                          ) : null}
                        </div>
                      </div>
                      <div className="flex items-center gap-2.5">
                        <Clock size={16} className={`${theme.icon} shrink-0`} />
                        <span>{b.hours}</span>
                      </div>
                    </div>
                  </div>

                  {/* Direct Map CTA Button */}
                  <div className="mt-8 border-t border-white/10 pt-4">
                    <a
                      href={mapUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center gap-2 w-full py-3 px-4 rounded-xl bg-white/5 hover:bg-white/10 text-white text-xs font-bold uppercase tracking-wider transition-colors border border-white/10"
                    >
                      <MapPin size={14} className={theme.icon} />
                      View on Google Maps
                    </a>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Divider Line */}
      <div className="w-full h-[1px] bg-gradient-to-r from-transparent via-hgold/40 to-transparent my-4 max-w-7xl mx-auto" />

      {/* Branch Glimpse Dual Marquee Section */}
      <section className="py-20 bg-[#080808] overflow-hidden">
        <motion.div
          initial={{ opacity: 0, y: -100 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: [0.25, 1, 0.5, 1] }}
          className="max-w-7xl mx-auto px-6 lg:px-10 mb-10 text-center"
        >
          <h2 className="text-4xl lg:text-5xl font-heading text-transparent bg-clip-text bg-gradient-to-r from-amber-300 via-hgold to-emerald-400 mb-3">
            Sensual Spaces: Glimpses of Hammam
          </h2>
          <div className="text-xs uppercase tracking-[0.25em] text-emerald-400/90 font-bold">Discreet. Rejuvenating. Premium.</div>
        </motion.div>

        <div className="relative w-full overflow-hidden py-4 space-y-6">
          {/* Left & Right Dark Shadow Vignettes */}
          <div className="absolute top-0 bottom-0 left-0 w-28 sm:w-48 bg-gradient-to-r from-[#080808] via-[#080808]/85 to-transparent z-10 pointer-events-none" />
          <div className="absolute top-0 bottom-0 right-0 w-28 sm:w-48 bg-gradient-to-l from-[#080808] via-[#080808]/85 to-transparent z-10 pointer-events-none" />

          {/* Row 1: Top Marquee (8 Images - Left to Right) */}
          <div className="flex gap-6 whitespace-nowrap animate-branch-marquee-reverse" style={{ width: "max-content" }}>
            {[
              "/branches/lajpat_nagar_1.jpeg",
              "/branches/lajpat_nagar_2.jpeg",
              "/branches/lajpat_nagar_3.jpeg",
              "/branches/lajpat_nagar_4.jpeg",
              "/branches/lajpat_nagar_5.jpeg",
              "/branches/rajouri_garden_1.jpeg",
              "/branches/rajouri_garden_2.jpeg",
              "/branches/rajouri_garden_3.jpeg",
              // Duplicated for seamless infinite loop
              "/branches/lajpat_nagar_1.jpeg",
              "/branches/lajpat_nagar_2.jpeg",
              "/branches/lajpat_nagar_3.jpeg",
              "/branches/lajpat_nagar_4.jpeg",
              "/branches/lajpat_nagar_5.jpeg",
              "/branches/rajouri_garden_1.jpeg",
              "/branches/rajouri_garden_2.jpeg",
              "/branches/rajouri_garden_3.jpeg",
            ].map((imgUrl, idx) => {
              const borderStyle = marqueeBorders[idx % marqueeBorders.length];
              return (
                <div key={idx} className={`relative rounded-2xl overflow-hidden ${borderStyle} group w-[300px] sm:w-[380px] shrink-0 inline-block`}>
                  <div className="aspect-[4/3] overflow-hidden bg-black/40">
                    <img src={imgUrl} alt={`Glimpse Top ${idx + 1}`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Row 2: Bottom Marquee (7 Images - Right to Left) */}
          <div className="flex gap-6 whitespace-nowrap animate-branch-marquee" style={{ width: "max-content" }}>
            {[
              "/branches/rajouri_garden_4.jpeg",
              "/branches/rajouri_garden_5.jpeg",
              "/branches/gurugram_1.jpeg",
              "/branches/gurugram_2.jpeg",
              "/branches/gurugram_3.jpeg",
              "/branches/gurugram_4.jpeg",
              "/branches/gurugram_5.jpeg",
              // Duplicated for seamless infinite loop
              "/branches/rajouri_garden_4.jpeg",
              "/branches/rajouri_garden_5.jpeg",
              "/branches/gurugram_1.jpeg",
              "/branches/gurugram_2.jpeg",
              "/branches/gurugram_3.jpeg",
              "/branches/gurugram_4.jpeg",
              "/branches/gurugram_5.jpeg",
            ].map((imgUrl, idx) => {
              const borderStyle = marqueeBorders[(idx + 2) % marqueeBorders.length];
              return (
                <div key={idx} className={`relative rounded-2xl overflow-hidden ${borderStyle} group w-[300px] sm:w-[380px] shrink-0 inline-block`}>
                  <div className="aspect-[4/3] overflow-hidden bg-black/40">
                    <img src={imgUrl} alt={`Glimpse Bottom ${idx + 1}`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  </div>
                </div>
              );
            })}
          </div>

          <style>{`
            .animate-branch-marquee {
              animation: branch-marquee-scroll 35s linear infinite;
            }
            .animate-branch-marquee-reverse {
              animation: branch-marquee-scroll-reverse 35s linear infinite;
            }
            @keyframes branch-marquee-scroll {
              0% { transform: translateX(0); }
              100% { transform: translateX(-50%); }
            }
            @keyframes branch-marquee-scroll-reverse {
              0% { transform: translateX(-50%); }
              100% { transform: translateX(0); }
            }
          `}</style>
        </div>
      </section>
    </div>
  );
}
